import { useState } from "react";
import {
    X,
    Edit3,
    User,
    Calendar,
    Flag,
    ClipboardList,
    Plus,
    ListChecks,
    Check,
    Trash2,
    CircleDashed,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import StatusPill from "@/components/StatusPill";
import PriorityPill from "@/components/PriorityPill";
import OwnerAvatar from "@/components/OwnerAvatar";

/**
 * Full-screen "expanded task" overlay for TV-readability.
 * Shows: group context, task pills, meta grid, notes, and inline subtasks
 * (add / toggle-done / rename / delete).
 */
export default function TaskExpandOverlay({
    open,
    task,
    group,
    subtasks,
    onClose,
    onEdit,
    onAddSubtask,
    onToggleSubtask,
    onDeleteSubtask,
    onRenameSubtask,
}) {
    const [newSubtaskName, setNewSubtaskName] = useState("");
    const [editingSubtaskId, setEditingSubtaskId] = useState(null);
    const [editingValue, setEditingValue] = useState("");

    const list = subtasks || task?.subtasks || [];
    const doneCount = list.filter((s) => s.done).length;

    const submitNew = () => {
        const name = newSubtaskName.trim();
        if (!name || !onAddSubtask) return;
        onAddSubtask(name);
        setNewSubtaskName("");
    };

    const startRename = (st) => {
        setEditingSubtaskId(st.id);
        setEditingValue(st.name);
    };
    const commitRename = () => {
        const name = editingValue.trim();
        const id = editingSubtaskId;
        setEditingSubtaskId(null);
        setEditingValue("");
        if (!id || !name || !onRenameSubtask) return;
        const current = list.find((s) => s.id === id);
        if (!current || current.name === name) return;
        onRenameSubtask(id, name);
    };

    return (
        <AnimatePresence>
            {open && task && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="fixed inset-0 z-[85] flex items-center justify-center bg-black/85 backdrop-blur-md"
                    onClick={onClose}
                    data-testid="task-expand-overlay"
                >
                    <motion.div
                        initial={{ y: 16, scale: 0.98 }}
                        animate={{ y: 0, scale: 1 }}
                        exit={{ y: 16, scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-[1200px] max-w-[92vw] max-h-[92vh] overflow-hidden bg-[#0A0A0A] border-2 border-virtus-orange flex flex-col"
                    >
                        {/* Corner brackets */}
                        <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-virtus-orange" />
                        <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-virtus-orange" />
                        <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-virtus-orange" />
                        <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-virtus-orange" />

                        {/* Header ribbon */}
                        <div className="flex items-center justify-between px-8 py-4 border-b border-virtus-line flex-shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="font-mono text-xs tracking-[0.28em] uppercase text-virtus-orange">
                                    TASK #{String(task.index || 0).padStart(2, "0")}
                                </div>
                                {group && (
                                    <>
                                        <span className="text-virtus-line">//</span>
                                        <div
                                            className="font-mono text-xs tracking-[0.24em] uppercase"
                                            style={{ color: group.color }}
                                        >
                                            {group.name}
                                        </div>
                                    </>
                                )}
                            </div>
                            <button
                                onClick={onClose}
                                className="flex items-center gap-2 px-3 py-2 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim hover:text-virtus-orange border border-virtus-line hover:border-virtus-orange"
                                data-testid="task-expand-close-btn"
                            >
                                <X size={12} /> CLOSE
                            </button>
                        </div>

                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto">
                            {/* Big title */}
                            <div className="px-8 pt-6 pb-3">
                                <h1
                                    className="font-heading font-black uppercase tracking-tighter leading-none text-white"
                                    style={{ fontSize: "clamp(40px, 5.2vw, 72px)" }}
                                >
                                    {task.name}
                                </h1>
                            </div>

                            {/* Pills row */}
                            <div className="px-8 pb-6 flex items-center gap-4">
                                <div className="scale-[1.5] origin-left">
                                    <StatusPill value={task.status} />
                                </div>
                                <div className="scale-[1.5] origin-left ml-8">
                                    <PriorityPill value={task.priority} />
                                </div>
                            </div>

                            {/* Grid of details */}
                            <div className="grid grid-cols-4 gap-px bg-virtus-line border-t border-b border-virtus-line">
                                <div className="bg-virtus-bg p-6">
                                    <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint mb-3">
                                        <User size={12} />
                                        Owner
                                    </div>
                                    {task.owner ? (
                                        <OwnerAvatar
                                            owner={task.owner}
                                            size={40}
                                            textClassName="text-white font-bold text-lg"
                                        />
                                    ) : (
                                        <div className="font-mono text-2xl font-bold text-virtus-dim">
                                            Unassigned
                                        </div>
                                    )}
                                </div>
                                <DetailBlock icon={Calendar} label="Start" value={task.start_date || "—"} />
                                <DetailBlock
                                    icon={Calendar}
                                    label="Due"
                                    value={task.due_date || "—"}
                                    accent={overdueColor(task.due_date)}
                                />
                                <DetailBlock
                                    icon={Flag}
                                    label="Priority"
                                    value={(task.priority || "medium").toUpperCase()}
                                />
                            </div>

                            {/* Subtasks section */}
                            <div className="px-8 pt-6 pb-2">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim">
                                        <ListChecks size={12} />
                                        SUBTASKS
                                    </div>
                                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-faint">
                                        [{doneCount}/{list.length}]
                                    </div>
                                    {list.length > 0 && (
                                        <div className="flex-1 h-[3px] bg-virtus-line ml-2">
                                            <div
                                                className="h-full bg-virtus-orange transition-all"
                                                style={{
                                                    width: `${list.length ? (doneCount / list.length) * 100 : 0}%`,
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="border border-virtus-line divide-y divide-virtus-line">
                                    {list.length === 0 && (
                                        <div className="px-4 py-3 font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-faint">
                                            &gt; No subtasks yet. Add one below.
                                        </div>
                                    )}
                                    {list.map((st) => (
                                        <div
                                            key={st.id}
                                            className="flex items-center gap-3 px-4 py-2 group hover:bg-[#141414]"
                                            data-testid={`subtask-row-${st.id}`}
                                        >
                                            <button
                                                onClick={() =>
                                                    onToggleSubtask && onToggleSubtask(st.id, !st.done)
                                                }
                                                aria-label={st.done ? "Mark as not done" : "Mark as done"}
                                                data-testid={`subtask-toggle-${st.id}`}
                                                className="flex-shrink-0"
                                            >
                                                {st.done ? (
                                                    <div className="w-5 h-5 flex items-center justify-center border border-virtus-orange bg-virtus-orange">
                                                        <Check size={12} className="text-black" />
                                                    </div>
                                                ) : (
                                                    <div className="w-5 h-5 border border-virtus-line hover:border-virtus-orange" />
                                                )}
                                            </button>
                                            {editingSubtaskId === st.id ? (
                                                <input
                                                    autoFocus
                                                    value={editingValue}
                                                    onChange={(e) => setEditingValue(e.target.value)}
                                                    onBlur={commitRename}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            commitRename();
                                                        } else if (e.key === "Escape") {
                                                            setEditingSubtaskId(null);
                                                            setEditingValue("");
                                                        }
                                                    }}
                                                    className="flex-1 bg-[#141414] border border-virtus-orange px-2 py-1 text-white outline-none"
                                                    data-testid={`subtask-name-input-${st.id}`}
                                                />
                                            ) : (
                                                <div
                                                    onClick={() => startRename(st)}
                                                    className={`flex-1 text-base cursor-text px-1 hover:bg-[#141414] hover:outline hover:outline-1 hover:outline-virtus-line ${
                                                        st.done
                                                            ? "line-through text-virtus-dim"
                                                            : "text-white"
                                                    }`}
                                                    title="Click to rename"
                                                    data-testid={`subtask-name-${st.id}`}
                                                >
                                                    {st.name}
                                                </div>
                                            )}
                                            {st.owner && (
                                                <div className="font-mono text-[11px] text-virtus-dim">
                                                    {st.owner}
                                                </div>
                                            )}
                                            <button
                                                onClick={() => onDeleteSubtask && onDeleteSubtask(st.id)}
                                                className="opacity-0 group-hover:opacity-100 text-virtus-faint hover:text-virtus-stuck transition-opacity"
                                                aria-label="Delete subtask"
                                                data-testid={`subtask-delete-${st.id}`}
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {/* Inline add-subtask form */}
                                <div className="flex items-center gap-2 mt-3">
                                    <CircleDashed size={16} className="text-virtus-faint" />
                                    <input
                                        value={newSubtaskName}
                                        onChange={(e) => setNewSubtaskName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                submitNew();
                                            }
                                        }}
                                        placeholder="Add a subtask and press Enter"
                                        className="flex-1 bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none text-base placeholder:text-virtus-faint"
                                        data-testid="subtask-new-input"
                                    />
                                    <button
                                        onClick={submitNew}
                                        disabled={!newSubtaskName.trim()}
                                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-2 hover:bg-virtus-orange hover:text-black disabled:opacity-40 disabled:cursor-not-allowed"
                                        data-testid="subtask-add-btn"
                                    >
                                        <Plus size={11} /> ADD
                                    </button>
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="px-8 py-6">
                                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim mb-3">
                                    <ClipboardList size={12} />
                                    NOTES
                                </div>
                                <div className="text-base leading-relaxed text-white whitespace-pre-wrap min-h-[60px]">
                                    {task.notes && task.notes.trim()
                                        ? task.notes
                                        : "No notes on record."}
                                </div>
                            </div>
                        </div>

                        {/* Footer actions */}
                        <div className="flex items-center justify-between px-8 py-4 border-t border-virtus-line flex-shrink-0">
                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint">
                                LAST UPDATE:{" "}
                                <span className="text-virtus-dim">
                                    {task.updated_at
                                        ? task.updated_at.replace("T", " ").slice(0, 19) + "Z"
                                        : "—"}
                                </span>
                            </div>
                            <button
                                onClick={onEdit}
                                className="flex items-center gap-2 px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase bg-virtus-orange text-black hover:bg-white glow-orange"
                                data-testid="task-expand-edit-btn"
                            >
                                <Edit3 size={12} /> EDIT
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function DetailBlock({ icon: Icon, label, value, accent }) {
    return (
        <div className="bg-virtus-bg p-6">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint mb-3">
                <Icon size={12} />
                {label}
            </div>
            <div className="font-mono text-2xl font-bold" style={{ color: accent || "#FFFFFF" }}>
                {value}
            </div>
        </div>
    );
}

function overdueColor(dueDate) {
    if (!dueDate) return null;
    try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = new Date(dueDate);
        if (due < today) return "#FF1744";
        const diff = (due - today) / (1000 * 60 * 60 * 24);
        if (diff <= 3) return "#FFEA00";
    } catch (_) {}
    return null;
}
